<template>
  <div :class="['hero', {hasAdaptiveColor}, `text-${props.textAlign}`]">
    <figure v-if="$slots.figure" class="figure">
      <slot name="figure"></slot>
    </figure>

    <header v-if="heading || subline" class="header">
      <component :is="headingTag" v-clean-html="heading" class="heading" />
      <component :is="sublineTag" v-clean-html="subline" class="h5 wrapper__subline" />
    </header>

    <footer v-if="$slots.actions" class="footer">
      <slot name="actions"></slot>
    </footer>

    <div v-if="$slots.quickFacts" class="quick-facts">
      <ul class="quick-facts-list">
        <slot name="quickFacts"></slot>
      </ul>
    </div>
  </div>
</template>


<script setup>
import { TextAlignTypes, HeadlineTagTypes } from '../../../constants/enums'

const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  headingTag: {
    type: String,
    default: 'h1',
    validator: (value) => HeadlineTagTypes.includes(value),
  },
  subline: {
    type: String,
    default: '',
  },
  sublineTag: {
    type: String,
    default: 'h2',
    validator: (value) => HeadlineTagTypes.includes(value),
  },
  textAlign: {
    type: String,
    default: 'left',
    validator: (value) => TextAlignTypes.includes(value),
  },
  hasAdaptiveColor: {
    type: Boolean,
    default: false,
  },
})
</script>


<style scoped lang='scss'>
@use '../../../styles' as index;

.hero {
  display: grid;
  grid-template-rows: repeat(9, auto);
  grid-template-columns: 100%;
  position: relative;
  width: 100%;
  height: auto;
  max-width: var(--maxContainerWidth);
  margin: 0 auto 3rem;

  & :not(.figure) {
    z-index: 1;
  }

  @media screen and (min-width: index.$breakpoint--l) {
    &.hasAdaptiveColor :is(.header, .quick-facts) {
      mix-blend-mode: difference;
    }

    &.hasAdaptiveColor :is(.header, .quick-facts) * {
      color: var(--white);
    }
  }
}

.figure {
  position: relative;
  inset: 0;
  margin: 0;
  grid-column: 1;
  grid-row: 1 / span 5;

  @media screen and (min-width: index.$breakpoint--l) {
    grid-row: 1 / span 7;
  }

  & :is(img, video) {
    object-fit: cover;
    object-position: right center;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;

    @media screen and (min-width: index.$breakpoint--s) {
      aspect-ratio: 1 / 0.375;
    }
  }
}

.header,
.footer {
  padding-inline: 1rem;
}

.header {
  grid-column: 1;
  grid-row: 6;

  @media screen and (min-width: index.$breakpoint--m) {
    padding-block-start: 1rem;
  }

  @media screen and (min-width: index.$breakpoint--l) {
    grid-column: 1;
    grid-row: 1 / span 2;
    text-align: left;
    padding-block-start: 2rem;
  }

  @media screen and (min-width: index.$breakpoint--xl) {
    max-width: 50%;
    padding-left: 6rem;
  }

  & p {
    margin-block: 0;
  }
}

.footer {
  display: flex;
  width: 100%;
  gap: 1rem;
  align-items: center;
  flex-direction: column;
  grid-column: 1;
  grid-row: 10;
  place-self: center;
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media screen and (min-width: index.$breakpoint--l) {
    grid-column: 1;
    grid-row: 3;
    place-self: start;
  }

  @media screen and (min-width: index.$breakpoint--s) {
    flex-flow: row wrap;
  }

  @media screen and (min-width: index.$breakpoint--xl) {
    padding-inline: 6rem;
  }
}

.quick-facts {
  grid-column: 1;
  grid-row: 8;
  place-self: center;
  padding: 1rem 1rem 0;

  & .quick-facts-list {
    list-style: none;
    padding: 0;

    & > * {
      display: flex;
      gap: 0.5rem;
      padding-left: 25px;

      > :deep(.icon) {
        height: 1lh;
        align-items: center;

        &:first-child {
          flex-shrink: 0;
      }}
    }
  }

  @media screen and (min-width: index.$breakpoint--l) {
    display: inline-flex;
    grid-column: 1;
    place-self: start;
  }

  @media screen and (min-width: index.$breakpoint--s) {
    padding: 0 1rem 0;

    & .quick-facts-list {
      display: inline-flex;
      flex-wrap: nowrap;
      gap: 0.5rem 2rem;
    }
  }

  @media screen and (min-width: index.$breakpoint--xl) {
    padding: 0 6rem 0;
  }
}

.text-left {
  .text-optional,
  .quick-facts,
  .footer {
    place-self: flex-start;
  }
}

.text-right {
  .text-optional,
  .quick-facts,
  .footer {
    place-self: flex-end;
  }
}
</style>
